import { useSortable } from '@dnd-kit/sortable'
import { GripHorizontal, X } from 'lucide-react'
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { cellClass, type GridColumns } from '../layout/grid'
import { clampSize, GRID_GAP, ROW_HEIGHT } from '../layout/model'
import type { WidgetConfig, WidgetSize } from '../types'
import { WidgetRenderer } from '../widgets/WidgetRenderer'

type CanvasWidgetProps = {
  widget: WidgetConfig
  cols: GridColumns
  selected: boolean
  placeholder: boolean
  onSelect: () => void
  onRemove: () => void
  onResize: (size: { w: WidgetSize; h: WidgetSize }) => void
}

const CORNERS = ['-left-1 -top-1', '-bottom-1 -left-1']
const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

export function CanvasWidget({ widget, cols, selected, placeholder, onSelect, onRemove, onResize }: CanvasWidgetProps) {
  const { t } = useTranslation()
  const title = t(`dashboard.widgets.${widget.type}.title`)
  const { attributes, listeners, setNodeRef } = useSortable({ id: widget.id })
  const cellRef = useRef<HTMLDivElement | null>(null)
  const [resizing, setResizing] = useState(false)
  const canResize = cols === 4

  function startResize(e: ReactPointerEvent) {
    e.preventDefault()
    e.stopPropagation()
    const grid = cellRef.current?.parentElement
    if (!grid) return
    const colWidth = (grid.clientWidth - GRID_GAP * (cols - 1)) / cols
    const start = { x: e.clientX, y: e.clientY, w: widget.w, h: widget.h }
    let last = { w: widget.w, h: widget.h }
    setResizing(true)

    const move = (ev: PointerEvent) => {
      const next = {
        w: clampSize(start.w + (ev.clientX - start.x) / (colWidth + GRID_GAP), cols),
        h: clampSize(start.h + (ev.clientY - start.y) / (ROW_HEIGHT + GRID_GAP)),
      }
      if (next.w !== last.w || next.h !== last.h) {
        last = next
        onResize(next)
      }
    }
    const up = () => {
      setResizing(false)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  if (placeholder) {
    return (
      <div
        ref={(node) => {
          setNodeRef(node)
          cellRef.current = node
        }}
        className={cn('min-w-0 rounded-2xl border-2 border-dashed border-primary/50 bg-primary/[0.05]', cellClass(widget.w, widget.h, cols))}
      />
    )
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        cellRef.current = node
      }}
      {...attributes}
      {...listeners}
      aria-label={t('dashboard.edit.widgetHandle', { title })}
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'group/cell relative min-w-0 cursor-grab rounded-2xl outline-none active:cursor-grabbing',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        cellClass(widget.w, widget.h, cols),
      )}
    >
      <div inert className="pointer-events-none h-full select-none">
        <WidgetRenderer config={widget} />
      </div>

      <div
        className={cn(
          'pointer-events-none absolute inset-0 rounded-2xl ring-inset transition-shadow',
          selected ? 'ring-2 ring-primary' : 'ring-1 ring-transparent group-hover/cell:ring-primary/40',
        )}
      />

      {selected ? (
        <>
          {CORNERS.map((pos) => (
            <span key={pos} className={cn('pointer-events-none absolute size-2.5 rounded-[3px] border-2 border-primary bg-card', pos)} />
          ))}
          <span className="pointer-events-none absolute -top-2.5 left-1/2 flex h-5 -translate-x-1/2 items-center rounded-full bg-primary px-2 text-primary-foreground shadow-sm">
            <GripHorizontal className="size-3.5" />
          </span>
          {canResize ? (
            <button
              type="button"
              aria-label={t('dashboard.edit.resize')}
              onPointerDown={startResize}
              onMouseDown={stop}
              onTouchStart={stop}
              onKeyDown={stop}
              onClick={stop}
              className="absolute -bottom-1.5 -right-1.5 z-10 size-4 cursor-se-resize rounded-[4px] border-2 border-primary bg-primary shadow-sm"
            />
          ) : (
            <span className="pointer-events-none absolute -bottom-1 -right-1 size-2.5 rounded-[3px] border-2 border-primary bg-card" />
          )}
          <span
            className={cn(
              'pointer-events-none absolute bottom-2 left-2 rounded-md bg-foreground/85 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-background transition-opacity',
              resizing ? 'opacity-100' : 'opacity-0 group-hover/cell:opacity-100',
            )}
          >
            {widget.w} × {widget.h}
          </span>
        </>
      ) : null}

      <button
        type="button"
        aria-label={t('dashboard.edit.removeWidget', { title })}
        onPointerDown={stop}
        onMouseDown={stop}
        onTouchStart={stop}
        onKeyDown={stop}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        className={cn(
          'absolute -right-2 -top-2 z-10 flex size-6 items-center justify-center rounded-full border bg-card text-muted-foreground shadow-sm transition-opacity',
          'hover:border-destructive/40 hover:text-destructive focus-visible:opacity-100',
          selected ? 'opacity-100' : 'opacity-0 group-hover/cell:opacity-100',
        )}
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}
